import React, { createContext, useState, useContext, useEffect } from 'react';

// Import all language files
import en from '../translations/en.json';
import nl from '../translations/nl.json';
import de from '../translations/de.json';
import fr from '../translations/fr.json';

// Create a context for language settings
const LanguageContext = createContext();

// Available languages
const languages = {
  en: { name: 'English', translations: en, flag: '🇺🇰' },
  nl: { name: 'Nederlands', translations: nl, flag: '🇳🇱' },
  de: { name: 'Deutsch', translations: de, flag: '🇩🇪' },
  fr: { name: 'Français', translations: fr, flag: '🇫🇷' }
};

// Language Provider component
export const LanguageProvider = ({ children }) => {
  // Get the language from localStorage or use Nederlands as default
  const getBrowserLanguage = () => {
    return 'nl'; // Always default to Nederlands
  };

  const [language, setLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem('qwixx_language');
    return savedLanguage || getBrowserLanguage();
  });

  // Update localStorage when language changes
  useEffect(() => {
    localStorage.setItem('qwixx_language', language);
  }, [language]);

  // Get the translations for the current language
  const translations = languages[language]?.translations || en;

  // Format a translation string with variables
  const formatTranslation = (text, variables) => {
    if (!text) return '';
    if (!variables) return text;

    return Object.entries(variables).reduce((result, [key, value]) => {
      return result.replace(new RegExp(`{${key}}`, 'g'), value);
    }, text);
  };

  // Function to get a translation by key
  const t = (key, variables) => {
    // Handle nested keys with dot notation (e.g., 'common.loading')
    const keys = key.split('.');
    let translation = translations;

    for (let k of keys) {
      translation = translation?.[k];
      if (translation === undefined) {
        console.warn(`Translation missing for key: ${key}`);
        return key; // Return the key as fallback
      }
    }

    return formatTranslation(translation, variables);
  };

  // Change the language
  const changeLanguage = (lang) => {
    if (languages[lang]) {
      setLanguage(lang);
    } else {
      console.warn(`Language ${lang} not supported`);
    }
  };

  // Get available languages for dropdown
  const getAvailableLanguages = () => {
    return Object.entries(languages).map(([code, { name, flag }]) => ({
      code,
      name,
      flag
    }));
  };

  // The context value
  const contextValue = {
    language,
    changeLanguage,
    t,
    getAvailableLanguages,
    currentLanguageInfo: languages[language]
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use the language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext; 